export type AddressParts = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

export const EMPTY_ADDRESS: AddressParts = {
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
};

/**
 * Joins the structured address fields into the single comma-separated string the
 * backend stores (e.g. `property_contracts.property_address`).
 */
export const combineAddress = (parts: AddressParts): string =>
  [parts.address1, parts.address2, parts.city, parts.state, parts.zip]
    .map((part) => (part ?? "").trim())
    .filter((part) => part !== "")
    .join(", ");

/**
 * Reverses {@link combineAddress} so a stored string can be edited field-by-field.
 * zip/state/city are positional from the end; everything before them is the street
 * address (address1 plus an optional, possibly comma-containing address2).
 */
export const parseAddress = (value?: string | null): AddressParts => {
  const parts = (value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");

  if (parts.length === 0) return { ...EMPTY_ADDRESS };

  // Partial input (street + maybe city/state/zip): fill from the front rather
  // than guessing positional fields that aren't present.
  if (parts.length < 4) {
    const [address1 = "", city = "", state = "", zip = ""] = parts;
    return { ...EMPTY_ADDRESS, address1, city, state, zip };
  }

  const zip = parts.pop() ?? "";
  const state = parts.pop() ?? "";
  const city = parts.pop() ?? "";
  const address1 = parts.shift() ?? "";
  const address2 = parts.join(", ");
  return { address1, address2, city, state, zip };
};
